<template>
  <NotFoundBlock
    v-if="pageErr"
    :api-url="apiUrl"
    :sogu-url="soguServiceUrl"
  />

  <template v-else>
    <SchemaOrg
      :city-slug="slug"
      :seo="seoData"
    />
    <Breadcrumbs
      v-show="!isOpenMapModal"
      :list="list"
    />
    <Header
      v-show="!isOpenMapModal"
      :title="title"
      :subtitle="subtitle"
    />
    <div>
      <SightsList
        v-model="isOpenMapModal"
        :city="cityData"
      />
      <OtherSights
        v-show="!isOpenMapModal"
        is-nearest-city-excursions
      />
      <FAQ
        v-show="!isOpenMapModal"
        :title="faqTitle"
        :faq="faqList"
      />
      <SubscriptionBlock
        v-show="!isOpenMapModal"
        :api-url="apiUrl"
        :analytics-api-url="soguServiceUrl"
      />
      <Seo
        v-if="seoLargeText && !isOpenMapModal"
        :text="seoLargeText"
      />
    </div>
  </template>
</template>

<script lang="ts" setup>
  import { NotFoundBlock } from '@experience/site-404-package';
  import { SubscriptionBlock } from '@experience/subscription-block-package';
  import { useHead } from '@unhead/vue';
  import { onMounted, onUnmounted, ref, watch, computed, nextTick } from 'vue';
  import { useRoute } from 'vue-router';

  import FAQ from '@/features/FAQ.vue';
  import OtherSights from '@/features/OtherSights.vue';
  import SchemaOrg from '@/features/SchemaOrg.vue';
  import Header from '@/features/SightsHeader.vue';
  import SightsList from '@/features/SightsListV2.vue';
  import { trackEvent } from '@/services/analytics';
  import { getSigthsSeo } from '@/shared/api/generated/api';
  import { getRuntimeConfig } from '@/shared/config/runtime';
  import { useRenderReport } from '@/shared/lib/lifecycle';
  import { type FaqItem, type AttractionData, type City } from '@/shared/models/faq';
  import { type BreadcrumbsList } from '@/shared/models/types';
  import { useSeoStore } from '@/shared/store/seo';
  import Breadcrumbs from '@/shared/ui/SightsBreadcrumbs.vue';
  import Seo from '@/shared/ui/SightsSeo.vue';

  const apiUrl = import.meta.env.VITE_API_URL;
  const { VITE_RUNTIME_SOGU_SERVICE_URL: soguServiceUrl } = getRuntimeConfig();

  useRenderReport();

  const route = useRoute();

  const slug = route.params.slug as string;

  const title = ref('');
  const subtitle = ref('');
  const seoLargeText = ref('');
  const faqTitle = ref('');
  const faqList = ref<FaqItem[]>([]);
  const list = ref<BreadcrumbsList[]>([]);
  const isOpenMapModal = ref(false);
  const cityData = ref<City>();
  const seoStore = useSeoStore();
  const pageErr = ref(false);
  const fullWidthMapBtn = ref<{ $el: HTMLElement } | null>(null);
  const isFullWidthBtnVisible = ref(false);
  let observer: IntersectionObserver | null = null;

  const sogu = () => {
    void trackEvent('attractions.pageview');
  };

  const fetchSeo = async (): Promise<void> => {
    if (!slug) {
      return;
    }

    try {
      const { data, response } = await getSigthsSeo({ path: { city: slug } });

      if (response.status !== 200) {
        throw new Error('Error');
      }

      if (!data) {
        return;
      }

      useData(data as unknown as AttractionData);
    } catch {
      pageErr.value = true;
    }
  };

  const seoData = ref<AttractionData['seo'] | null>(null);

  const headData = computed(() => {
    if (!seoData.value) {
      return {};
    }

    return {
      title: seoData.value.title,
      link: [
        {
          href: seoData.value.canonical_url,
          rel: 'canonical' as const,
        },
      ],
      meta: [
        {
          content: seoData.value.description,
          name: 'description' as const,
        },
        {
          content: seoData.value.description,
          property: 'og:description' as const,
        },
      ],
    };
  });

  useHead(headData);

  const useData = (data: AttractionData) => {
    const { seo, faq, city } = data;

    seoData.value = seo ?? null;

    cityData.value = city;
    title.value = seo?.h1 ?? '';
    subtitle.value = seo?.h2 ?? '';
    seoLargeText.value = seo?.text ?? '';

    faqTitle.value = faq?.title ?? '';
    faqList.value = faq?.items ?? [];

    const { country } = city;

    seoStore.$patch({
      geo: {
        city_id: city.id,
        city_name: city.name_ru,
        country_id: country?.id,
        country_name: country?.name_ru,
      },
    });

    sogu();

    list.value = [
      {
        label: 'Главная',
        href: '/',
      },
      {
        label: country?.name_ru ?? '',
        href: `/destinations/${country?.slug ?? ''}/`,
      },
      {
        label: city.name_ru,
        href: `/experience/${city.slug}/`,
      },
      {
        label: 'Достопримечательности',
      },
    ];
  };

  // Стартуем запрос сразу в setup, не дожидаясь монтирования
  void fetchSeo();

  onMounted(async () => {
    // Отслеживание видимости полноширинной кнопки
    await nextTick();

    if (fullWidthMapBtn.value?.$el) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            isFullWidthBtnVisible.value = entry.isIntersecting;
          });
        },
        {
          threshold: 0.1,
        },
      );

      observer.observe(fullWidthMapBtn.value.$el);
    }
  });

  onUnmounted(() => {
    if (observer) {
      observer.disconnect();
    }
  });

  watch(isOpenMapModal, (value) => {
    const header = document.getElementsByClassName('site-header') as HTMLCollectionOf<HTMLElement>;
    const footer = document.getElementsByClassName('footer-wrapper') as HTMLCollectionOf<HTMLElement>;

    const headerElement = header[0];
    const footerElement = footer[0];

    if (headerElement) {
      headerElement.style.display = value ? 'none' : 'block';
    }

    if (footerElement) {
      footerElement.style.display = value ? 'none' : 'block';
    }
  });
</script>
